import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Section, SectionHeader } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTASection } from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { listTeam, type PublicTeamMember } from "@/lib/content.functions";
import { TeamMemberModal } from "@/components/TeamMemberModal";

export const Route = createFileRoute("/equipe")({
  head: () => ({
    meta: [
      { title: "Nossa Equipe | Especialistas em CRM com IA e Automação" },
      { name: "description", content: "Conheça a equipe da SVG Multimídia: especialistas em CRM com IA, automação de atendimento no WhatsApp, inteligência artificial para vendas e consultoria comercial." },
      { property: "og:title", content: "Nossa Equipe | Especialistas em CRM com IA e Automação" },
      { property: "og:description", content: "Time especializado em CRM com IA, automação de atendimento no WhatsApp e consultoria comercial que estrutura o funil até o fechamento." },
      { property: "og:url", content: "/equipe" },
    ],
    links: [{ rel: "canonical", href: "https://www.svgmultimidia.com.br/equipe" }],
  }),
  component: Equipe,
});

function initials(name: string) {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function Equipe() {
  const { data: members = [], isLoading } = useQuery({
    queryKey: ["team"],
    queryFn: () => listTeam(),
    staleTime: 5 * 60 * 1000,
  });
  const [active, setActive] = useState<PublicTeamMember | null>(null);

  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Equipe" }]} />
          <div className="mt-6 max-w-4xl">
            <span className="eyebrow">Quem faz acontecer</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
              <span className="text-gradient">Nossa Equipe</span>
            </h1>
            <p className="mt-4 text-muted-foreground md:text-lg">
              A SVG Multimídia reúne especialistas em IA aplicada a processos comerciais: CRM com inteligência artificial, automação de atendimento no WhatsApp, estruturação de funil de vendas e consultoria comercial. Cada profissional atua em uma etapa essencial para ajudar empresas a atenderem 24 horas, organizarem leads, recuperarem oportunidades paradas e transformarem conversas em vendas previsíveis.
            </p>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeader eyebrow="Time SVG" title="Especialistas em busca, conteúdo e conversão" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="card-glass h-72 animate-pulse" aria-hidden />
              ))
            : members.map((m, idx) => (
                <Reveal key={m.id} delay={(idx % 4) as 0 | 1 | 2 | 3}>
                  <Tilt3D>
                    <button
                      type="button"
                      onClick={() => setActive(m)}
                      aria-label={`Ver mais sobre ${m.name}`}
                      className="card-glass flex w-full flex-col items-start text-left transition hover:border-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <div className="mb-4 grid h-28 w-28 place-items-center overflow-hidden rounded-2xl bg-[image:var(--gradient-primary)] text-2xl font-bold text-primary-foreground ring-2 ring-ink/10 shadow-lg">
                        {m.photo_url ? (
                          <img src={m.photo_url} alt={`Foto de ${m.name}`} className="h-full w-full object-cover" loading="lazy" width={112} height={112} />
                        ) : (
                          <span aria-hidden>{initials(m.name)}</span>
                        )}
                      </div>
                      <h2 className="text-lg font-semibold">{m.name}</h2>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-primary">{m.role}</p>
                      {m.bio && <p className="mt-3 line-clamp-4 text-sm text-muted-foreground">{m.bio}</p>}
                      <span className="mt-4 text-xs font-semibold text-primary">Ver perfil completo →</span>
                    </button>
                  </Tilt3D>
                </Reveal>
              ))}
        </div>
      </Section>

      <TeamMemberModal member={active} onClose={() => setActive(null)} />
      <CTASection />
      {members.length > 0 && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "SVG Multimídia",
              employee: members.map((m) => ({
                "@type": "Person",
                name: m.name,
                jobTitle: m.role,
                ...(m.photo_url ? { image: m.photo_url } : {}),
                ...(m.bio ? { description: m.bio } : {}),
                worksFor: { "@type": "Organization", name: "SVG Multimídia" },
              })),
            }),
          }}
        />
      )}
    </>
  );
}